import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { useMediaById } from '@lib/hooks/useMediaById';
import { useMediaThumbnail } from '@lib/image/thumbnail';
import { handleAuthedChatClick } from '@lib/network/chats';
import { getPodcastProjectUrl, PodcastInstance } from '@lib/network/podcasts';
import { formatCreditsWithUnit } from '@lib/utils/units';
import { useState } from 'react';
import { PodcastStatusChip } from '../PodcastStatusChip';
import css from './PodcastListItem.module.scss';
import { PodcastWidgets } from './PodcastWidgets';

/**
 * One row in the podcast projects list. Reads its thumbnail from the
 * project's first source media. Configuration pills come from the
 * sibling `PodcastWidgets` component, which reads `starterVariables`
 * directly.
 *
 * Click navigates to the project's page on the StarZero Agents app
 * (same window) — until we ship an in-app detail page.
 */
export const PodcastListItem = ({ instance }: { instance: PodcastInstance }) => {
  const sourceLibraryId = instance.setup?.libraryId;
  const sourceMediaId = instance.setup?.selectedContentIds?.[0];
  const [thumbnailError, setThumbnailError] = useState(false);
  const fetched = useMediaThumbnail(sourceLibraryId, sourceMediaId);
  const thumbnailUrl = thumbnailError ? undefined : (fetched?.url ?? undefined);
  const thumbnailToken = fetched?.token ?? undefined;

  // Title fallback chain mirrors vibedit's PodcastListItem:
  // prefer the auto-generated `instance.name`, else look up the source
  // media's metadata.name, else show a placeholder.
  const sourceMedia = useMediaById(sourceLibraryId, sourceMediaId);
  const title = instance.name || sourceMedia?.metadata?.name || '...';
  const status = instance.status ?? 'queued';
  const vars = instance.starterVariables;
  const expectedClips = vars?.clip_count ?? instance.expectedOutputCount ?? 0;
  const href = getPodcastProjectUrl(instance._id);

  return (
    <a
      className={css.row}
      href={href || '#'}
      // Tokenless href + click interceptor: the auth token is only added
      // at navigation time, so right-click "Copy link" and DOM inspection
      // don't expose it. Middle-click / cmd-click fall through to the
      // browser default and rely on chat's own redirect handoff.
      onClick={handleAuthedChatClick(href, {
        surface: 'podcasts-page',
        control: 'podcast-list-row',
        podcastId: instance._id,
      })}
      // Same-window navigation — the rest of the workspace's podcast
      // links use this pattern, and the deep-link target is on a
      // sibling app the user is already authenticated to.
    >
      <div className={css.thumbnail}>
        {thumbnailUrl ? (
          <AuthenticatedImage
            src={thumbnailUrl}
            token={thumbnailToken}
            alt=""
            onError={() => setThumbnailError(true)}
            className={css.thumbnailImg}
          />
        ) : (
          <div className={css.thumbnailPlaceholder} aria-hidden="true" />
        )}
      </div>
      <div className={css.body}>
        <div className={css.headerRow}>
          <h3 className={css.title}>{title}</h3>
          <PodcastStatusChip status={status} />
        </div>
        <div className={css.subtitle}>
          {expectedClips > 0 && (
            <span>
              {expectedClips} clip{expectedClips === 1 ? '' : 's'}
            </span>
          )}
          {instance.creditsUsed != null && instance.isDone && (
            <>
              {expectedClips > 0 && (
                <span className={css.dot} aria-hidden="true">
                  ·
                </span>
              )}
              <span>{formatCreditsWithUnit(instance.creditsUsed)}</span>
            </>
          )}
        </div>
        {vars && <PodcastWidgets vars={vars} />}
      </div>
    </a>
  );
};
